/* eslint-disable no-bitwise */

import { keys } from 'lodash';

export default class DateWrapper {
  static WEEKDAY = 0x00000001;
  static YEAR = 0x00000010;
  static MONTH = 0x00000100;
  static DAY = 0x00001000;
  static HOUR = 0x00010000;
  static MINUTE = 0x00100000;
  static SECOND = 0x01000000;
  static DAY_ORDINAL = 0x10000000;

  static _locale = '';

  constructor(_date) {
    if (typeof _date === 'object') {
      this.date = _date;
    } else if (typeof _date === 'string') {
      this.date = new Date(_date);
    } else {
      this.date = new Date();
    }
  }

  /**
   * Set the date locale
   * @param {*} _locale the locale to translate date string
   */
  static locale(_locale) {
    DateWrapper._locale = _locale;
  }

  /**
   * Set the date locale
   * @param {*} _locale the locale to translate date string
   */
  locale(_locale) {
    this._locale = _locale;
  }

  /**
   * Gets a date attribute
   * @param {*} _attribute the attribute to fetch.
   * @returns {*} the fetched value
   */
  get(_attribute) {
    const attributeMapping = {
      day: this.date.getDate.bind(this.date),
      month: this.date.getMonth.bind(this.date),
      year: this.date.getFullYear.bind(this.date),
      hour: this.date.getHours.bind(this.date),
      minute: this.date.getMinutes.bind(this.date),
      second: this.date.getSeconds.bind(this.date),
      timeZoneOffset: () => this._timeZoneOffset
    };
    if (keys(attributeMapping).indexOf(_attribute) === -1) {
      return null;
    }
    return attributeMapping[_attribute]();
  }

  /**
   * Sets a date attribute.
   * @param {*} _attribute the attribute to set.
   * @param {*} _value the value to set to.
   * @returns {*} the current DateWrapper instance for chaining set() calls
   */
  set(_attribute, _value) {
    const attributeMapping = {
      day: this.date.setDate.bind(this.date),
      month: this.date.setMonth.bind(this.date),
      year: this.date.setFullYear.bind(this.date),
      hour: this.date.setHours.bind(this.date),
      minute: this.date.setMinutes.bind(this.date),
      second: this.date.setSeconds.bind(this.date),
      timeZoneOffset: offset => {
        this._timeZoneOffset = offset;
      }
    };
    if (keys(attributeMapping).indexOf(_attribute) === -1) {
      return null;
    }
    attributeMapping[_attribute](_value);
    return this;
  }

  /**
   * Determines if two date attributes are the same.
   * @param {*} _date the other date wrapper instance
   * @param {*} _option the attribute to compare with
   * @returns {*} true if both attributes are equal
   */
  isSame(_date, _option) {
    return _date.get(_option) === this.get(_option);
  }

  /**
   * Get date string based on locale, if _locale is not passed, use the
   * locale set.
   * @param {*} _options bitmask option to determine which date attribute
   * to include in the string
   * @param {*} _locale (optional) the locale to translate the date
   * string to
   * @returns {*} the translated date string based on locale
   */
  toLocaleString(_options, _locale) {
    const locale = _locale || this._locale || DateWrapper._locale || 'en-US';
    const options = {};
    const tmzAwareDate = new Date();
    const utcTime = this.date.getTime() + this.date.getTimezoneOffset() * 60000;
    const tmzTime = utcTime + (this._timeZoneOffset || 0) * 60000;
    tmzAwareDate.setTime(tmzTime);

    const getSuffix = () => {
      let suffix = '';
      if ((_options & DateWrapper.DAY_ORDINAL) > 0 && locale.split('-')[0] === 'en') {
        const dayString = tmzAwareDate.toLocaleString(locale, { day: 'numeric' });
        const dayMatch = /\d+/.exec(dayString); // IE11 inserts zero-width LTR or RTL mark that must be ignored
        if (dayMatch) {
          const day = parseInt(dayMatch[0], 10);
          suffix = 'th';
          [1, 21, 31].indexOf(day) !== -1 && (suffix = 'st');
          [2, 22].indexOf(day) !== -1 && (suffix = 'nd');
          [3, 23].indexOf(day) !== -1 && (suffix = 'rd');
        }
      }
      return suffix;
    };

    if ((_options & DateWrapper.WEEKDAY) > 0) {
      options.weekday = 'short';
    }
    if ((_options & DateWrapper.YEAR) > 0) {
      options.year = 'numeric';
    }
    if ((_options & DateWrapper.MONTH) > 0) {
      options.month = 'short';
    }
    if ((_options & DateWrapper.DAY) > 0 || (_options & DateWrapper.DAY_ORDINAL) > 0) {
      options.day = 'numeric';
    }
    if ((_options & DateWrapper.HOUR) > 0) {
      options.hour = 'numeric';
    }
    if ((_options & DateWrapper.MINUTE) > 0) {
      options.minute = 'numeric';
    }
    if ((_options & DateWrapper.SECOND) > 0) {
      options.second = 'numeric';
    }
    const suffix = getSuffix();
    const dateString = tmzAwareDate.toLocaleString(locale, options);
    const dateMatch = /\d+/.exec(dateString);
    return suffix && dateMatch
      ? dateString.replace(dateMatch[0], `${dateMatch[0]}${suffix}`)
      : dateString;
  }

  toISOString() {
    return this.date.toISOString();
  }
}
